import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { useWishlist } from '../../contexts/WishlistContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';

const WishlistButton = ({ 
  product, 
  size = 20, 
  className = '', 
  showText = false, 
  variant = 'default',
  disabled = false 
}) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled || isLoading) return;
    
    if (!isAuthenticated) {
      // Reuse cart auth prompt modal
      await addToCart(product, 1);
      return;
    }

    console.log('🔍 WishlistButton click:', { productId: product.id, product, isInWishlistState });
    
    setIsLoading(true);
    try {
      const result = await toggleWishlist(product.id);
      console.log('✅ Toggle wishlist result:', result);
    } catch (error) {
      console.error('❌ Erreur wishlist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isInWishlistState = isInWishlist(product.id);

  // Variants de style
  const getVariantClasses = () => {
    switch (variant) {
      case 'minimal':
        return `p-1.5 rounded-full transition-all duration-200 ${
          isInWishlistState
            ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30' 
            : 'bg-gray-900/20 text-gray-400 hover:bg-gray-900/40 hover:text-gray-300'
        }`;
      
      case 'filled':
        return `p-2 rounded-full transition-all duration-200 ${
          isInWishlistState
            ? 'bg-red-500 text-white hover:bg-red-600 shadow-lg shadow-red-500/25' 
            : 'bg-gray-900/80 text-gray-300 hover:bg-gray-900 hover:text-white'
        }`;
      
      case 'outline':
        return `p-2 rounded-full border-2 transition-all duration-200 ${
          isInWishlistState
            ? 'border-red-500 text-red-500 hover:bg-red-500 hover:text-white' 
            : 'border-gray-400 text-gray-400 hover:border-gray-300 hover:text-gray-300'
        }`;
      
      default:
        return `p-2 rounded-full transition-all duration-200 ${
          isInWishlistState
            ? 'bg-red-500 text-white hover:bg-red-600' 
            : 'bg-gray-900/80 text-gray-300 hover:bg-gray-900 hover:text-white'
        }`;
    }
  };

  return (
    <button 
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={`
        ${getVariantClasses()}
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
        group
      `}
      title={
        isInWishlistState 
          ? 'Retirer de la liste de souhaits' 
          : 'Ajouter à la liste de souhaits'
      }
      aria-label={
        isInWishlistState 
          ? 'Retirer de la liste de souhaits' 
          : 'Ajouter à la liste de souhaits'
      }
    >
      <div className="flex items-center space-x-2">
        <Heart 
          size={size} 
          className={`
            transition-all duration-200
            ${isInWishlistState ? 'fill-current scale-110' : 'group-hover:scale-110'}
            ${isLoading ? 'animate-pulse' : ''}
          `}
        />
        {showText && (
          <span className="text-sm font-medium whitespace-nowrap">
            {isInWishlistState ? 'Retirer' : 'Ajouter'}
          </span>
        )}
      </div>
    </button>
  );
};

export default WishlistButton;
